import { useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { submitSepaPayout } from '../api'
import { isLikelyIban } from '../_shared/format'
import { loadToken, type DecodedToken } from '../_shared/token'
import {
  Badge,
  Button,
  Card,
  ErrorPanel,
  Field,
  Footer,
  FullPageLoader,
  Header,
  Page,
  TextInput,
} from '../_shared/ui'

export default function SepaForm() {
  const { surveyId = '' } = useParams()
  const navigate = useNavigate()
  const [search] = useSearchParams()
  const [token, setToken] = useState<string | null>(null)
  const [decoded, setDecoded] = useState<DecodedToken | null>(null)
  const [bootstrapped, setBootstrapped] = useState(false)

  const [iban, setIban] = useState('')
  const [accountHolder, setAccountHolder] = useState('')
  const [addressLine1, setAddressLine1] = useState('')
  const [city, setCity] = useState('')
  const [postalCode, setPostalCode] = useState('')
  const [country, setCountry] = useState('')
  const [consent, setConsent] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [fieldErrors, setFieldErrors] = useState<{
    iban?: string
    accountHolder?: string
    addressLine1?: string
    city?: string
    postalCode?: string
    country?: string
    consent?: string
  }>({})
  const [submitError, setSubmitError] = useState<string | null>(null)

  useEffect(() => {
    const { token: t, decoded: d } = loadToken(surveyId, search.toString())
    setToken(t)
    setDecoded(d)
    setBootstrapped(true)
  }, [surveyId, search])

  if (!bootstrapped) return <FullPageLoader />

  if (!token || !decoded || decoded.expired) {
    return (
      <Page>
        <Header />
        <ErrorPanel
          title="Session expired"
          body="Please reopen the payment link your sponsor sent you."
        />
        <Footer surveyId={surveyId} />
      </Page>
    )
  }

  const ibanValid = !iban || isLikelyIban(iban)

  function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    const errs: typeof fieldErrors = {}
    if (!iban.trim()) errs.iban = 'Required'
    else if (!isLikelyIban(iban)) errs.iban = 'IBAN checksum failed — double-check the digits'
    if (!accountHolder.trim() || accountHolder.trim().length < 2) errs.accountHolder = 'Enter your full name as it appears on the bank account'
    if (!addressLine1.trim() || addressLine1.trim().length < 2) errs.addressLine1 = 'Required for SEPA transfers'
    if (!city.trim()) errs.city = 'Required'
    if (!postalCode.trim()) errs.postalCode = 'Required'
    if (!/^[A-Za-z]{2}$/.test(country.trim())) errs.country = 'Two-letter country code, e.g. DE'
    if (!consent) errs.consent = 'You must consent to submit'
    setFieldErrors(errs)
    if (Object.keys(errs).length > 0) return

    setSubmitting(true)
    setSubmitError(null)
    submitSepaPayout({
      surveyId,
      panellistToken: token!,
      tier: decoded!.tier,
      iban,
      accountHolder,
      addressLine1: addressLine1.trim(),
      city: city.trim(),
      postalCode: postalCode.trim(),
      country: country.trim().toUpperCase(),
      consent,
    }).then((res) => {
      setSubmitting(false)
      if (!res.ok) {
        setSubmitError(humanError(res.error))
        return
      }
      navigate(`/p/${surveyId}/confirm`, { state: res.data })
    })
  }

  return (
    <Page>
      <Header
        surveyName="Bank transfer (SEPA)"
        sponsorName={`Tier: ${decoded.tier}`}
        badge={<Badge variant="info">EUR · ~15s</Badge>}
      />

      <form onSubmit={onSubmit}>
        <Card>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--cp-sp-4)' }}>
            <Field
              label="IBAN"
              hint="Your full IBAN, e.g. DE89 3704 0044 0532 0130 00"
              error={fieldErrors.iban}
            >
              <TextInput
                value={iban}
                onChange={setIban}
                placeholder="DE89..."
                autoComplete="off"
                spellCheck={false}
                invalid={!ibanValid}
                maxLength={42}
              />
            </Field>

            <Field
              label="Account holder name"
              hint="Must match the name on the IBAN."
              error={fieldErrors.accountHolder}
            >
              <TextInput
                value={accountHolder}
                onChange={setAccountHolder}
                placeholder="Maria Garcia"
                autoComplete="name"
                maxLength={140}
              />
            </Field>

            <Field
              label="Street address"
              hint="Required by EU payment regulation for bank transfers."
              error={fieldErrors.addressLine1}
            >
              <TextInput
                value={addressLine1}
                onChange={setAddressLine1}
                placeholder="Hauptstraße 12"
                autoComplete="address-line1"
                maxLength={140}
              />
            </Field>

            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 'var(--cp-sp-3)' }}>
              <Field label="City" error={fieldErrors.city}>
                <TextInput
                  value={city}
                  onChange={setCity}
                  placeholder="Berlin"
                  autoComplete="address-level2"
                  maxLength={80}
                />
              </Field>
              <Field label="Postal code" error={fieldErrors.postalCode}>
                <TextInput
                  value={postalCode}
                  onChange={setPostalCode}
                  placeholder="10115"
                  autoComplete="postal-code"
                  maxLength={20}
                />
              </Field>
              <Field label="Country" error={fieldErrors.country}>
                <TextInput
                  value={country}
                  onChange={(v) => setCountry(v.toUpperCase())}
                  placeholder="DE"
                  autoComplete="country"
                  maxLength={2}
                  spellCheck={false}
                />
              </Field>
            </div>

            <label
              style={{
                display: 'flex',
                gap: 10,
                alignItems: 'flex-start',
                fontSize: 'var(--cp-text-sm)',
                color: 'var(--cp-fg-secondary)',
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                style={{ marginTop: 3 }}
              />
              <span>
                I confirm the IBAN above belongs to me and I consent to AsterPay processing my
                bank details for the sole purpose of this payment. I will receive a verifiable
                receipt.
              </span>
            </label>
            {fieldErrors.consent && (
              <span style={{ fontSize: 'var(--cp-text-sm)', color: 'var(--cp-danger)' }}>
                {fieldErrors.consent}
              </span>
            )}

            {submitError && (
              <div
                style={{
                  background: 'var(--cp-danger-bg)',
                  color: 'var(--cp-danger-dark)',
                  borderRadius: 'var(--cp-radius-md)',
                  padding: '10px 12px',
                  fontSize: 'var(--cp-text-sm)',
                }}
              >
                {submitError}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8 }}>
              <Button variant="ghost" onClick={() => navigate(`/p/${surveyId}`)} disabled={submitting}>
                Back
              </Button>
              <Button type="submit" loading={submitting} fullWidth>
                Submit payment details
              </Button>
            </div>
          </div>
        </Card>
      </form>

      <Footer surveyId={surveyId} />
    </Page>
  )
}

function humanError(err: string): string {
  switch (err) {
    case 'invalid_iban': return 'IBAN failed checksum validation. Please double-check the digits.'
    case 'invalid_account_holder': return 'Please enter your full name as it appears on the bank account.'
    case 'invalid_token': return 'Your payment link is no longer valid. Please contact your sponsor.'
    case 'token_survey_mismatch':
    case 'token_tier_mismatch': return 'The payment link does not match this survey or tier.'
    case 'survey_not_accepting_payouts': return 'This survey is no longer accepting payouts.'
    case 'method_not_enabled': return 'SEPA payouts are not enabled for this survey.'
    case 'already_submitted': return 'You have already submitted this payout. Check your email for the receipt.'
    case 'survey_not_configured': return 'This survey is not fully configured yet. Please contact the sponsor.'
    case 'consent_required': return 'You must check the consent box to submit.'
    case 'invalid_country': return 'Country must be a two-letter code, e.g. DE.'
    case 'address_required': return 'Street address, city, postal code and country are required for bank payouts.'
    case 'live_payouts_not_configured': return 'Live payouts are not configured for this survey yet. Please contact the sponsor.'
    case 'settlement_failed': return 'The bank transfer could not be initiated. Your details were recorded — the sponsor has been notified.'
    default: return `Could not submit (${err}). Please try again or contact the sponsor.`
  }
}
